import React, { useState } from 'react';
import { 
  SimpleGrid, 
  Skeleton, 
  Image, 
  AspectRatio, 
  Box,
  Icon,
  Text
} from '@chakra-ui/react';
import { FaPlay } from 'react-icons/fa';
import MediaModal from './MediaModal';

/**
 * Reusable grid component for displaying generation history
 * @param {GenerationCard[]} props.items – already mapped view-model with .url .type etc.
 * @param {number} props.limit – defaults to 12
 * @param {function} props.onItemSelect – optional callback when item is selected (replaces modal behavior)
 */
export default function RecentHistoryGrid({ items = [], limit = 12, onItemSelect }) {
  const [modalItem, setModalItem] = useState(null);
  
  const handleItemClick = (item) => {
    if (onItemSelect) {
      // Use custom selection behavior instead of modal
      onItemSelect(item);
    } else {
      // Default behavior: open modal
      setModalItem(item);
    }
  };
  
  const closeModal = () => setModalItem(null);

  return (
    <>
      <SimpleGrid
        columns={{ base: 2, sm: 3, md: 4 }}
        spacing={4}
        w="full"
      >
        {items.slice(0, limit).map((item) => (
          <AspectRatio
            key={item.id}
            ratio={1}
            border="1px solid"
            borderColor="green.700"
            borderRadius="md"
            cursor="pointer"
            overflow="hidden"
            position="relative"
            bg="rgba(0, 0, 0, 0.3)"
            _hover={{ 
              boxShadow: '0 0 8px var(--chakra-colors-green-400)',
              borderColor: "green.400",
              transform: "scale(1.02)"
            }}
            transition="all 0.3s ease"
            onClick={() => handleItemClick(item)}
          >
            <Skeleton isLoaded={!!item.url} w="100%" h="100%">
              {item.type === 'video' ? (
                <Box position="relative" w="100%" h="100%">
                  <video 
                    src={item.url} 
                    muted 
                    style={{ 
                      width: "100%", 
                      height: "100%", 
                      objectFit: "cover" 
                    }}
                  />
                  {/* Video play overlay */}
                  <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    bg="rgba(0, 0, 0, 0.7)"
                    borderRadius="full"
                    p={2}
                    color="green.400"
                  >
                    <Icon as={FaPlay} boxSize={4} />
                  </Box>
                </Box>
              ) : (
                <Box position="relative" w="100%" h="100%">
                  <Image 
                    src={item.url} 
                    alt={item.prompt || 'Generated image'} 
                    objectFit="cover"
                    w="100%"
                    h="100%"
                    fallbackSrc="/fallback-image.svg"
                  />
                  {/* Image type indicator */}

                </Box>
              )}
            </Skeleton>
          </AspectRatio>
        ))}
        
        {/* Show empty state if no items */}
        {items.length === 0 && (
          <Box
            gridColumn="1 / -1"
            textAlign="center"
            py={8}
            color="gray.500"
          >
            <Text>No generations found</Text>
          </Box>
        )}
      </SimpleGrid>

      {/* Modal for viewing/interacting with selected item - only when not in selection mode */}
      {!onItemSelect && modalItem && (
        <MediaModal
          isOpen={!!modalItem}
          onClose={closeModal}
          media={modalItem}
        />
      )}
    </>
  );
}
